import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { logoSvg } from 'assets/images';
import { dataFormat, formRequiredRules, getUtcDate, handleOptions } from 'helper';
import React, { useEffect, useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { InputRadio } from 'sharedComponents/inputGroup/InputRadio';
import { getCreditScoreForProve, postQuestions, softAssesment } from 'store/CreditScoreState';
import { RootState } from 'store/store';
import { DECISION } from '../CreditConstants';
import { Person, ProveCSReq, Question, QuestionData, QuestionFormData } from '../CreditScoreInterface';
import CustomLoader from 'sharedComponents/loader/CustomLoader';
import { getScoreByPriority } from '../CreditHelper';
import { CommonNumberInput } from 'sharedComponents/inputGroup/CommonNumberInput';
import { errorMessage } from 'constants/CommonConstants';
import useProveOtpNew from 'hooks/useProveOtpNew';
import Logger from 'utils/logger';

const Step3: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  const isMobile = true;

  // Hooks
  const { t } = useTranslation();
  const hasCalledNextStep = React.useRef(false); // useRef to store the flag
  const hasCalledMounted = React.useRef(false);
  const [resendDisabled, setResendDisabled] = React.useState(false);
  const [timer, setTimer] = React.useState(30);

  const { startProveVerification, otpActions, validationError, otpSent, ERROR_CODES } = useProveOtpNew();
  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const {
    publicLeadCreationData: {
      data: { Lead: { _id, leadId } = { _id: '', leadId: '' } } = { Lead: { _id: '', leadId: '' } },
    },
    publicLeadCreationData,
    creditScoreLoading,
    reportQuestions,
  } = useSelector((state: RootState) => state.creditScoreStore);

  const { proveLoading } = useSelector((state: RootState) => state.tradeInStore);

  const { control, trigger, getValues, setValue, setError } = useFormContext();

  const handlePostQuestions = async (personData: Person) => {
    try {
      const res = await dispatch(postQuestions({ person: personData }));

      Logger.custom('cs_decision', {
        id: leadId || '',
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Credit Score',
        decision: res?.payload?.message?.decision || 'not available / Rejected',
      });

      if (DECISION.includes(res?.payload?.message?.decision) && res?.payload?.message?.questions?.length > 0) {
        return 'success';
      } else {
        return 'error';
      }
    } catch (error) {
      console.error('Error in handlePostQuestions:', error);

      Logger.error('Error in handlePostQuestions:', {
        errorData: error?.message || error,
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Credit Score',
      });

      return 'error';
    }
  };

  useEffect(() => {
    if (hasCalledMounted.current) return;
    hasCalledMounted.current = true;

    proveVerification();
  }, []);

  const proveVerification = async () => {
    const { first, last, emailAddress, dateValue, address, address_city, address_region, address_postal, phone } =
      getValues();

    const date = getUtcDate(dateValue);

    const personData: Person = {
      name: {
        first: first,
        last: last,
        suffix: '',
      },
      emailAddress: emailAddress,
      dateOfBirth: {
        day: `${date.selectedDate}`.length === 1 ? `0${date.selectedDate}` : date.selectedDate?.toString(),
        month: `${date.month}`.length === 1 ? `0${date.month}` : date.month?.toString(),
        year: `${date.year}`,
      },
      address: [
        {
          type: 'STREET',
          recency: 'CURRENT',
          line1: address?.label,
          city: address_city,
          region: address_region,
          countryCode: 'CA',
          postalCode: address_postal,
        },
      ],
    };

    startProveVerification(isMobile, publicLeadCreationData?.data, phone, async () => {
      Logger.custom('prove_verification_completed', {
        id: leadId || '',
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Credit Score',
      });

      const reqPayload: ProveCSReq = {
        leadIdString: leadId,
        leadId: _id,
        userInfo: {
          firstName: first,
          lastName: last,
          dateOfBirth: dataFormat(dateValue, 'yyyy-MM-dd'),
          address: {
            streetName: address?.label,
            city: address_city,
            province: address_region,
            postalCode: address_postal,
          },
        },
      };

      const res = await dispatch(getCreditScoreForProve(reqPayload));
      const score = getScoreByPriority(res?.payload?.message?.creditScore);

      Logger.custom('credit_score_fetched', {
        id: leadId || '',
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Credit Score',
        score: score || 'not available',
      });

      const step = score > 0 ? 3 : 4;

      if (step === 4) {
        setValue('credit_score_error_heading', t('stepperFormCredit.errorMsg'));
      }

      onNext?.(step);
    }).catch(async (error) => {
      console.error('Error in startProveVerification:', error);

      Logger.error('Error in startProveVerification:', {
        errorData: error?.message || error,
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Credit Score',
      });

      const result = await handlePostQuestions(personData);

      if (result != 'success') {
        setValue('credit_score_error_heading', t('stepperFormCredit.errorMsg'));
        onNext?.(4);
      }
    });
  };

  useEffect(() => {
    if (validationError) {
      hasCalledNextStep.current = false;

      Logger.error('Prove validation error:', {
        errorData: validationError,
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
        module: 'Credit Score',
      });

      if (validationError != ERROR_CODES.AUTH_TOKEN_MISSING) {
        proveVerification();
      }

      if (otpSent) {
        setError('otp', {
          type: 'manual',
          message: t('stepperForm.invalidOtp'),
        });
      }

      // Display the error message to the user as needed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationError]);

  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    try {
      if (reportQuestions?.questions?.length > 0) {
        const data = getValues();

        const dynamicQuestions: Record<string, string> = Object.keys(data)
          .filter((key) => key.startsWith('questionId-'))
          .reduce((result: Record<string, string>, key) => {
            result[key] = data[key];
            return result;
          }, {});

        const updatedValues: QuestionFormData[] = Object.entries(dynamicQuestions).map(([key, value]) => ({
          questionId: +key.slice(key.indexOf('-') + 1),
          answer: [Number(value)],
        }));

        const questionData: QuestionData = {
          id: reportQuestions?._id,
          assessments: updatedValues,
          leadId: _id,
          leadIdString: leadId,
        };

        const res = await dispatch(softAssesment(questionData));
        const score = getScoreByPriority(res?.payload?.message?.creditScore);
        const step = DECISION.includes(res?.payload?.message?.decision) && score > 0 ? 3 : 4;

        Logger.custom('cs_assesment', {
          id: leadId || '',
          client: authenticateClientData?.data?.client || '',
          widgetName: authenticateClientData?.data?.widgetName || '',
          module: 'Credit Score',
          score: score || 'not available',
        });

        if (step === 4) {
          setValue('credit_score_error_heading', t('stepperFormCredit.errorMsg'));
          setValue('credit_score_error_desc', t('stepperFormCredit.errorDesc'));
        }

        onNext?.(step);
      } else {
        try {
          const { otp } = getValues();

          if (otpActions?.submitOtp) {
            Logger.custom('otp_submitted', {
              id: leadId || '',
              client: authenticateClientData?.data?.client || '',
              widgetName: authenticateClientData?.data?.widgetName || '',
              module: 'Credit Score',
            });
            otpActions?.submitOtp?.(otp);
          }
        } catch (error) {
          console.error('Error in nextStep:', error);
        }
      }
    } catch (error) {
      console.error('postSoftAssesment error', error);
    }
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  const handleResendOtp = () => {
    proveVerification();

    // Simulate sending a new OTP (you should replace this with your actual logic)

    // Disable the resend button
    setResendDisabled(true);

    // Start a timer for 30 seconds
    let countdown = 30;
    setTimer(countdown);

    const timerInterval = setInterval(() => {
      countdown -= 1;
      setTimer(countdown);

      // Enable the resend button when the timer reaches 0
      if (countdown <= 0) {
        clearInterval(timerInterval);
        setResendDisabled(false);
      }
    }, 1000);
  };

  return (
    <>
      <div className={`csc-screens__form-col shadow ${creditScoreLoading || proveLoading ? 'position-static' : ''}`}>
        {otpSent ? (
          <>
            <div className='trade-in-stepper__otp-form__content text-center'>
              <h4 className='trade-in-stepper__otp-form__content__title'>
                {t('stepperFormTradeIn.mobileVerification')}
              </h4>
              <p>{t('stepperFormTradeIn.note6')}</p>
            </div>
            <div className='form-group'>
              <CommonNumberInput
                name='otp'
                control={control}
                label={t('stepperFormTradeIn.enterCode')}
                placeholder={t('stepperFormTradeIn.enterCode')}
                rules={{
                  ...formRequiredRules(errorMessage(t).codeRequired),
                }}
                thousandSeparator={false}
                triggerValidationForField={trigger}
              />
            </div>

            {resendDisabled && <p className='resend-otp pb-3'> {t('stepperFormTradeIn.resendOtp', { timer })}</p>}
            <div className='form-group'>
              <div className='resend-otp'>
                {t('stepperFormTradeIn.didntReceiveCode')}
                <button className='resend-otp__cta' onClick={handleResendOtp} disabled={resendDisabled}>
                  {t('stepperFormTradeIn.resend')}
                </button>
              </div>
            </div>
          </>
        ) : null}
        {/* kountVerification start */}
        {reportQuestions?.questions?.length > 0 && (
          <>
            <div className='logo'>
              <img src={logoSvg} alt='Dabadu' className='img-fluid' />
            </div>
            <div className='csc-screens__form-col__top'>
              <h3 className='csc-screens__form-col__title'>{t('stepperForm.confirmIdentity')}</h3>
              <div className='note'>
                {t('stepperForm.heading4')}
                <span>{t('stepperForm.confirmIdentity')}</span>
              </div>
            </div>
            <div className='csc-screens__form-col__middle'>
              <div className='csc-screens__field-wrapper'>
                <ol className='list-css-counter radio-list list-unstyled mb-5'>
                  {reportQuestions &&
                    reportQuestions?.questions?.map((question: Question) => (
                      <li>
                        <InputRadio
                          // rules={formRequiredRules(errorMessage(t).radioError)}
                          name={`questionId-${question?.questionId}`}
                          control={control}
                          label={question?.questionText}
                          options={handleOptions(question?.choices)}
                          directionRow={false}
                          triggerValidationForField={trigger}
                        />
                      </li>
                    ))}
                </ol>
              </div>
            </div>
          </>
        )}
        {/* kountVerification end */}
        <CustomLoader show={creditScoreLoading || proveLoading} title={t('stepperFormCredit.loaderTxt2')} />
      </div>
    </>
  );
};

const StepperForm3 = React.forwardRef(Step3);

export default StepperForm3;
